import React, { useEffect, useState } from "react";
import { Button, ButtonVariant } from "@wartsila/ui-kit";
import { useInView } from "react-intersection-observer";
import { useLocation, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { ICustomerGroupInstallation, IEquipment, IUnplannedRequest } from "online-services-types";
import { Container } from "src/design-system/Container";
import { ButtonStyle } from "src/design-system/Button";
import { DropdownStyle, ISelectObject, Select } from "src/design-system/Select/Select";
import { CustomStyledTextInput, SearchButton } from "src/design-system/SearchInput";
import { InputLabel } from "src/design-system/TextInput";
import { RangePicker } from "src/design-system/RangePicker";
import { StyledFormField, StyledFormInputWrapper, StyledInputLabel } from "src/design-system/Form";
import { IconSize } from "src/design-system/Tokens/icons";
import { NoRequestsFoundIndicator } from "src/components/CommerceList/styles";
import { ConfirmDeleteDialog } from "src/components/SupportList/ConfirmDeleteDialog";
import { ResponsiveTable } from "src/components/ResponsiveTable";
import { SearchIcon, WarningIcon } from "src/icons";
import { translateString } from "src/util/localization";
import { dateFormat, formatEquipmentLabel } from "src/util/formatters";
import {
  useDeleteUnplannedRequest,
  usePreselectedUnplannedRequest,
  useUnplannedRequestFiltersDispatchProps,
  useUnplannedRequestFiltersStateProps,
  useUnplannedRequestRules,
  useUnplannedRequests,
  UNPLANNED_REQUESTS,
} from "./unplannedRequest.hooks";
import { FiltersWrapper, NotificationWrapper, ResetRangeButton } from "./unplannedRequest.styles";
import { UnplannedRequestFiltersProps, UnplannedRequestListProps } from "./unplannedRequest.types";
import { getColumns, UNPLANNED_REQUEST_STATUSES, UNPLANNED_REQUEST_TYPES } from "./unplannedRequest.utils";

export const UnplannedRequestFiltersComponent = ({ filters, setSingleFilter }: UnplannedRequestFiltersProps) => {
  const { accounts, installations, equipments, isAccountsReady, isInstallationsReady, isEquipmentsReady } =
    useUnplannedRequestFiltersStateProps();
  const { getAccounts, getInstallations, getEquipments } = useUnplannedRequestFiltersDispatchProps();
  const { data: rules } = useUnplannedRequestRules();
  const [poNumberInput, setPoNumberInput] = useState(filters.customerPONumber || "");

  const submitPONumber = () => setSingleFilter("customerPONumber", poNumberInput);

  useEffect(() => {
    if (!installations.length && isInstallationsReady) {
      getInstallations();
    }
    if (!accounts.length && isAccountsReady) {
      getAccounts();
    }
    if (!equipments.length && isEquipmentsReady) {
      getEquipments();
    }
  }, []);

  const allowedInstallationIds = rules?.[0]?.installations;
  const allowedEquipmentIds = rules?.[0]?.equipments;

  const filteredInstallations = allowedInstallationIds
    ? installations.filter((inst) => allowedInstallationIds.includes(inst.id))
    : installations;

  const filteredEquipments = allowedEquipmentIds
    ? equipments.filter((eq) => allowedEquipmentIds.includes(eq.id))
    : equipments;

  return (
    <FiltersWrapper>
      <Select
        isMulti
        isClearable
        isSearchable
        isLoading={!isInstallationsReady}
        options={filteredInstallations}
        value={filteredInstallations.filter((inst) => filters.installation?.includes(inst.salesforceId))}
        label={translateString("installation")}
        dropdownStyle={DropdownStyle.FullWidth}
        getOptionLabel={(option: ISelectObject<ICustomerGroupInstallation>) => option.item.name}
        getOptionValue={(option: ISelectObject<ICustomerGroupInstallation>) => option.item.salesforceId}
        onChange={(value: ICustomerGroupInstallation[] = []) =>
          setSingleFilter(
            "installation",
            value.map((val) => val.salesforceId)
          )
        }
      />
      <Select
        isMulti
        isClearable
        isSearchable
        isLoading={!isEquipmentsReady}
        options={filteredEquipments.filter((equipment) => {
          const selectedInstallations = filters.installation ?? [];
          if (selectedInstallations.length > 0) {
            return selectedInstallations.includes(equipment.installationSalesforceId);
          }
          return filteredInstallations.some((inst) => inst.salesforceId === equipment.installationSalesforceId);
        })}
        value={filteredEquipments.filter((eq) => filters.equipment?.includes(eq.id))}
        label={translateString("equipment")}
        dropdownStyle={DropdownStyle.FullWidth}
        getOptionLabel={(option: ISelectObject<IEquipment>) => formatEquipmentLabel(option.item)}
        getOptionValue={(option: ISelectObject<IEquipment>) => option.item.id}
        onChange={(value: IEquipment[] = []) =>
          setSingleFilter(
            "equipment",
            value.map((val) => val.id)
          )
        }
      />
      {UNPLANNED_REQUEST_TYPES.length > 1 && (
        <Select
          isMulti
          isClearable
          isSearchable
          options={UNPLANNED_REQUEST_TYPES}
          value={filters.requestType}
          label={translateString("contracts.unplannedRequests.requestType")}
          dropdownStyle={DropdownStyle.FullWidth}
          getOptionLabel={(option: ISelectObject<string>) => option.item}
          getOptionValue={(option: ISelectObject<string>) => option.item}
          onChange={(value: string[] = []) => setSingleFilter("requestType", value)}
        />
      )}
      <Select
        isMulti
        isClearable
        isSearchable
        options={UNPLANNED_REQUEST_STATUSES}
        value={filters.status}
        label={translateString("contracts.unplannedRequests.requestStatus")}
        dropdownStyle={DropdownStyle.FullWidth}
        getOptionLabel={(option: ISelectObject<string>) => option.item}
        getOptionValue={(option: ISelectObject<string>) => option.item}
        onChange={(value: string[] = []) => setSingleFilter("status", value)}
      />
      <StyledFormField>
        <InputLabel>{translateString("contracts.unplannedRequests.poNumber")}</InputLabel>
        <StyledFormInputWrapper>
          <CustomStyledTextInput
            type="search"
            placeholder={translateString("typeToSearch")}
            value={poNumberInput}
            onChange={(e) => {
              setPoNumberInput(e.target.value);
              // Catching the click on the X
              if (!e.target.value) setSingleFilter("customerPONumber", "");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitPONumber();
            }}
          />
          <SearchButton
            onClick={submitPONumber}
            buttonStyle={ButtonStyle.Icon}
            icon={<SearchIcon size={IconSize.Small} />}
          />
        </StyledFormInputWrapper>
      </StyledFormField>
      <StyledFormField>
        <StyledInputLabel>{translateString("contracts.unplannedRequests.submittedDate")}</StyledInputLabel>
        <RangePicker
          startDate={filters.submittedDateFrom ? moment(filters.submittedDateFrom) : undefined}
          endDate={filters.submittedDateTo ? moment(filters.submittedDateTo) : undefined}
          onChange={(range) => {
            const [startDate, endDate] = range;
            setSingleFilter("submittedDateFrom", startDate?.format(dateFormat));
            setSingleFilter("submittedDateTo", endDate?.format(dateFormat));
            return range;
          }}
          placeholder={translateString("documents.select")}
          maxDate={moment()}
        />
        <ResetRangeButton
          type="button"
          onClick={() => {
            setSingleFilter("submittedDateFrom", "");
            setSingleFilter("submittedDateTo", "");
          }}
        >
          {translateString("documents.resetRange")}
        </ResetRangeButton>
      </StyledFormField>
    </FiltersWrapper>
  );
};

export const UnplannedRequestListComponent = ({ filters, showPreselected }: UnplannedRequestListProps) => {
  const { unplannedRequestId } = useParams<{ unplannedRequestId?: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { ref, inView } = useInView({ initialInView: true });
  const { data: preselectedUnplannedRequest } = usePreselectedUnplannedRequest(
    unplannedRequestId,
    Boolean(unplannedRequestId && showPreselected)
  );
  const {
    data: unplannedRequests,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useUnplannedRequests(filters);
  const { mutate: deleteRequest } = useDeleteUnplannedRequest({ filters });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | undefined>();

  useEffect(() => {
    queryClient.resetQueries([UNPLANNED_REQUESTS]);
  }, [location.key]);

  useEffect(() => {
    if (inView) fetchNextPage();
  }, [inView]);

  const allUnplannedRequests = showPreselected
    ? [
        ...(preselectedUnplannedRequest || []),
        ...(unplannedRequests?.pages?.flat() || []).filter(
          (unplannedRequest) => unplannedRequest.id !== unplannedRequestId
        ),
      ]
    : unplannedRequests?.pages?.flat();

  if (!allUnplannedRequests?.length && !isFetching && !isFetchingNextPage) {
    return (
      <NoRequestsFoundIndicator>
        {translateString("contracts.unplannedRequests.noRequestsFound")}
      </NoRequestsFoundIndicator>
    );
  }

  return (
    <>
      <Container $marginBottom={2}>
        <ResponsiveTable<IUnplannedRequest>
          rows={allUnplannedRequests?.length ? allUnplannedRequests : undefined}
          columns={getColumns(setConfirmDeleteId)}
          isItemOpen={(unplannedRequest) => unplannedRequest.id === unplannedRequestId && showPreselected}
          ensureVisibleId={showPreselected ? unplannedRequestId : undefined}
          displayedItemId={showPreselected ? unplannedRequestId : undefined}
          scrollToOpenItem={showPreselected}
          getHasAlert={(req) => req.status === "Information Needed" || req.status === "Waiting for PO"}
          rowExtension={(req) => {
            if (req.status === "Information Needed") {
              return (
                <NotificationWrapper $padding={[2, 3]} $centered>
                  <WarningIcon size={IconSize.Small} />
                  {translateString("contracts.unplannedRequests.informationNeededNotice")}
                </NotificationWrapper>
              );
            }
            if (req.status === "Waiting for PO") {
              return (
                <NotificationWrapper $padding={[2, 3]} $centered>
                  <WarningIcon size={IconSize.Small} />
                  {translateString("contracts.unplannedRequests.waitingForPONotice")}
                </NotificationWrapper>
              );
            }
            return undefined;
          }}
        />
      </Container>
      <ConfirmDeleteDialog
        isOpen={Boolean(confirmDeleteId)}
        onOk={() => {
          if (confirmDeleteId) deleteRequest(confirmDeleteId);
          setConfirmDeleteId(undefined);
        }}
        onCancel={() => setConfirmDeleteId(undefined)}
      />

      {hasNextPage ? (
        <Button ref={ref} variant={ButtonVariant.Warning} loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
          {translateString("main.loadMore")}
        </Button>
      ) : null}
    </>
  );
};
